<template>
  <div class="min-h-screen bg-gray-50 light" style="color-scheme: light;">
    <!-- Sidebar -->
    <nav 
      class="fixed top-0 left-0 h-full bg-white border-r border-gray-200 shadow-sm transition-all duration-300"
      :class="[sidebarOpen ? 'w-64' : 'w-0']"
    >
      <div class="p-4 border-b border-gray-200" v-show="sidebarOpen">
        <h2 class="text-xl font-bold text-gray-800">{{ $t('nav.admin_dashboard') }}</h2>
      </div>
      <ul class="space-y-1 p-4" v-show="sidebarOpen">
        <li v-for="item in menuItems" :key="item.to">
          <NuxtLink 
            :to="localePath(item.to)" 
            class="flex items-center p-2 rounded-lg transition-colors duration-150"
            :class="[
              isCurrentPath(item.to)
                ? 'bg-primary-50 text-primary-700 font-medium'
                : 'text-gray-700 hover:bg-gray-50 hover:text-primary-600'
            ]"
          >
            <Icon :name="item.icon" class="w-5 h-5 mr-2" />
            {{ $t(item.label) }}
          </NuxtLink>
        </li>
      </ul>
    </nav>

    <!-- Main content -->
    <div 
      :class="[sidebarOpen ? 'ml-64' : 'ml-0']" 
      class="transition-all duration-300 min-h-screen flex flex-col"
    >
      <header class="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div class="flex justify-between items-center px-6 py-4">
          <div class="flex items-center gap-4">
            <button 
              @click="toggleSidebar" 
              class="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
              :title="sidebarOpen ? 'Hide sidebar' : 'Show sidebar'"
            >
              <Icon 
                :name="sidebarOpen ? 'heroicons:bars-3-bottom-left' : 'heroicons:bars-3'" 
                class="w-6 h-6"
              />
            </button>
            <h1 class="text-xl font-semibold text-gray-800">{{ pageTitle }}</h1>
          </div>
          <div class="flex items-center space-x-4">
            <!-- Language Selector -->
            <ClientOnly>
              <div class="relative inline-block">
                <select
                  v-model="locale"
                  class="pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-gray-700 hover:bg-gray-50 cursor-pointer appearance-none"
                >
                  <option value="en" class="bg-white hover:bg-gray-50">English</option>
                  <option value="ja" class="bg-white hover:bg-gray-50">日本語</option>
                </select>
                <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
                  <Icon
                    :name="locale === 'en' ? 'twemoji:flag-united-kingdom' : 'twemoji:flag-japan'"
                    class="w-5 h-5"
                  />
                </div>
              </div>
            </ClientOnly>

            <ClientOnly>
              <span class="text-gray-700">{{ username }}</span>
            </ClientOnly>
            <UButton 
              @click="handleLogout" 
              class="bg-white hover:bg-primary-50 text-gray-700 border border-gray-300 rounded-lg"
              icon="i-heroicons-arrow-right-on-rectangle"
            >
              {{ $t('auth.logout') }}
            </UButton>
          </div>
        </div>
      </header>

      <div class="flex-1 p-6">
        <slot :sidebar-open="sidebarOpen" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { DropdownItem } from '#ui/types'

const { t, locale } = useI18n()
const localePath = useLocalePath()
const route = useRoute()
const authStore = useAuthStore()

const currentLocale = computed(() => locale.value)

const adminPath = computed(() => route.path.replace(/^\/ja(?=\/)/, ''))

const pageTitle = computed(() => {
  const path = adminPath.value

  if (path.includes('/admin/users/create')) {
    return t('admin.users.create.title')
  }
  if (/\/admin\/users\/[^/]+\/edit/.test(path)) {
    return t('admin.users.edit.title')
  }
  if (path.includes('/admin/locks/create')) {
    return t('admin.locks.create.title')
  }
  if (/\/admin\/locks\/[^/]+\/edit/.test(path)) {
    return t('admin.locks.edit.title')
  }
  if (path.includes('/admin/bikes/create')) {
    return t('admin.bikes.form.create.title')
  }
  if (/\/admin\/bikes\/[^/]+\/edit/.test(path)) {
    return t('admin.bikes.edit.title')
  }
  if (path.includes('/admin/locations/create')) {
    return t('admin.locations.create.title')
  }
  if (/\/admin\/locations\/[^/]+\/edit/.test(path)) {
    return t('admin.locations.edit.title')
  }
  if (path.includes('/admin/changelog')) {
    return t('admin.changelog.title')
  }

  switch (path) {
    case '/admin':
      return t('admin.menu.dashboard')
    case '/admin/users':
      return t('admin.users.title')
    case '/admin/reservations':
      return t('admin.reservations.title')
    case '/admin/locks':
      return t('admin.locks.title')
    case '/admin/bikes':
      return t('admin.navigation.bikes')
    case '/admin/locations':
      return t('admin.locations.title')
    case '/admin/reports':
      return t('admin.navigation.reports')
    case '/admin/content':
      return t('admin.menu.content')
    case '/admin/settings':
      return t('admin.settings.title')
    default:
      return t('nav.admin_dashboard')
  }
})
const username = computed(() => authStore.user?.username ?? t('common.guest'))

const languageItems = computed(() => [[
  {
    label: t('languages.en'),
    icon: 'twemoji:flag-united-kingdom',
    click: () => locale.value = 'en'
  },
  {
    label: t('languages.ja'),
    icon: 'twemoji:flag-japan',
    click: () => locale.value = 'ja'
  }
]])

const menuItems = [
  {
    to: '/admin',
    icon: 'heroicons:home',
    label: 'admin.menu.dashboard'
  },
  {
    to: '/admin/users',
    icon: 'heroicons:users',
    label: 'admin.menu.users'
  },
  {
    to: '/admin/reservations',
    icon: 'heroicons:calendar',
    label: 'admin.menu.reservations'
  },
  {
    to: '/admin/locks',
    icon: 'heroicons:lock-closed',
    label: 'admin.menu.locks'
  },
  {
    to: '/admin/bikes',
    icon: 'heroicons:rocket-launch',
    label: 'admin.menu.bikes'
  },
  {
    to: '/admin/locations',
    icon: 'heroicons:map',
    label: 'admin.menu.locations'
  },
  {
    to: '/admin/reports',
    icon: 'heroicons:chart-bar',
    label: 'admin.menu.reports'
  },
  {
    to: '/admin/changelog',
    icon: 'heroicons:document-text',
    label: 'admin.menu.changelog'
  },
  {
    to: '/admin/settings',
    icon: 'heroicons:cog-6-tooth',
    label: 'admin.menu.settings'
  }
]

const isCurrentPath = (path: string) => {
  return route.path.startsWith(localePath(path))
}

const handleLogout = () => {
  authStore.logout()
  navigateTo(localePath('/login'))
}

const sidebarOpen = ref(true)

const toggleSidebar = () => {
  sidebarOpen.value = !sidebarOpen.value
}
</script>

<style scoped>
/* Force light styling for dropdowns */
:deep(.ui-dropdown) {
  background-color: white !important;
  border-color: #e5e7eb !important;
}

:deep(.ui-dropdown-item) {
  background-color: white !important;
  color: #374151 !important;
}

:deep(.ui-dropdown-item:hover) {
  background-color: #f9fafb !important;
  color: #1f2937 !important;
}

:deep(.ui-dropdown-item.active) {
  background-color: #ecfdf5 !important;
  color: #059669 !important;
}

:deep(.ui-dropdown-container) {
  background-color: white !important;
  border-color: #e5e7eb !important;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
}
</style> 